/**
 * Edit a deal.
 *
 * Stage is not here: it moves through the detail side panel, because entering a
 * losing stage requires a reason in the same request. `updateDealInput` omits
 * `statusId` entirely, so submitting one would be rejected rather than silently
 * ignored.
 */

import { Form, redirect, useActionData, useLoaderData, useNavigation } from 'react-router-dom'
import type { ActionFunctionArgs, LoaderFunctionArgs } from 'react-router-dom'
import { updateDealInput, type Deal } from '@erp/contracts'
import type { Contact, Organization } from '@erp/contracts'
import { api, ApiError } from '../api'
import { invalid, issuesFrom, type FormResult } from '../forms'
import { DealFields, readDealForm } from '../components/deal-form'
import { Button, PageHeader } from '../ui'

interface Data {
  deal: Deal
  organizations: Organization[]
  contacts: Contact[]
}

export async function loader({ params }: LoaderFunctionArgs): Promise<Data> {
  const id = params.dealId
  if (!id) throw new Response('Missing deal id', { status: 400 })

  const [deal, organizations, contacts] = await Promise.all([
    api.deals.get(id),
    api.organizations.list({ limit: 200, sort: 'name', direction: 'asc' }),
    api.contacts.list({ limit: 200, sort: 'lastName', direction: 'asc' }),
  ])
  return { deal, organizations: organizations.rows, contacts: contacts.rows }
}

export async function action({ request, params }: ActionFunctionArgs): Promise<FormResult | Response> {
  const id = params.dealId
  if (!id) throw new Response('Missing deal id', { status: 400 })

  const parsed = updateDealInput.safeParse(readDealForm(await request.formData()))
  if (!parsed.success) return invalid(issuesFrom(parsed.error))

  try {
    await api.deals.update(id, parsed.data)
    return redirect(`/crm/deals/${id}`)
  } catch (error) {
    if (error instanceof ApiError) return invalid(error.issues, error.message)
    throw error
  }
}

export default function EditDeal() {
  const { deal, organizations, contacts } = useLoaderData() as Data
  const result = useActionData() as FormResult | undefined
  const navigation = useNavigation()
  const saving = navigation.state === 'submitting'

  const organizationName = organizations.find((o) => o.id === deal.organizationId)?.name

  return (
    <>
      <PageHeader title={`Edit ${organizationName ?? 'deal'}`} />
      {result?.message ? <div className="ax-alert ax-alert-danger">{result.message}</div> : null}

      <Form method="post" className="ax-form">
        <DealFields
          deal={deal}
          organizations={organizations}
          contacts={contacts}
          issues={result?.issues ?? {}}
        />
        <div className="ax-form-actions">
          <Button variant="solid" type="submit" disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
          <a className="ax-btn" href={`/crm/deals/${deal.id}`}>
            Cancel
          </a>
        </div>
      </Form>
    </>
  )
}
